import { api, sessionMayExist } from "./client";

/*
 * What `auth.me` rejects with when there is plainly no one to ask about — shaped like the axios
 * 401 the server would have sent, so callers that read `response.status` treat both the same.
 */
const signedOut = () => {
  const err = new Error("Not signed in");
  err.response = { status: 401, data: null };
  err.signedOut = true;
  return Promise.reject(err);
};

export const auth = {
  login: (email, password) =>
    api.post("/api/auth/login", { email, password }).then((r) => r.data),
  refresh: () => api.post("/api/auth/refresh").then((r) => r.data),
  logout: () => api.post("/api/auth/logout").then((r) => r.data),
  // Skipped for a visitor with no sign of a session: the answer is certainly 401, and the browser
  // would log that normal case as a console error on every public page (see sessionMayExist).
  me: () => (sessionMayExist() ? api.get("/api/users/me").then((r) => r.data) : signedOut()),
  updateMe: (patch) => api.patch("/api/users/me", patch).then((r) => r.data),
  /*
   * The profile photo. Both resolve the updated user, so the caller can show the new photo (or the
   * initials) everywhere at once without asking /me again.
   *
   * The Content-Type is left for the browser to write: a multipart body is only readable with the
   * boundary the browser picked, and a hand-set "multipart/form-data" has none, which the server
   * rejects as an empty upload. axios drops its JSON default for FormData on its own.
   *
   * A longer timeout than the shared fifteen seconds, because an upload on a weak phone signal is
   * the one request here whose size the user controls. `onProgress` gets 0–1.
   */
  uploadAvatar: (file, onProgress) => {
    const body = new FormData();
    body.append("file", file, file.name || "avatar.jpg");
    return api
      .put("/api/users/me/avatar", body, {
        timeout: 60000,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.min(1, e.loaded / e.total));
        },
      })
      .then((r) => r.data);
  },
  removeAvatar: () => api.delete("/api/users/me/avatar").then((r) => r.data),
  // Irreversible. The password goes in the body — axios needs `data` for DELETE, which is easy to
  // miss because it silently sends nothing if you pass the object positionally like a POST.
  deleteAccount: (password) =>
    api.delete("/api/users/me", { data: { password } }).then((r) => r.data),
  // Password reset (two-phase OTP): request a code, verify it for a reset token, then set the password.
  forgotPassword: (email) =>
    api.post("/api/auth/forgot-password", { email }).then((r) => r.data),
  verifyResetOtp: (email, code) =>
    api.post("/api/auth/verify-reset-otp", { email, code }).then((r) => r.data),
  resetPassword: (resetToken, newPassword) =>
    api.post("/api/auth/reset-password", { resetToken, newPassword }).then((r) => r.data),
};

export const rides = {
  list: (params) => api.get("/api/rides", { params }).then((r) => r.data),
  get: (id) => api.get(`/api/rides/${id}`).then((r) => r.data),
  create: (payload) => api.post("/api/rides", payload).then((r) => r.data),
  // Partial by design: send only what changed. The server refuses the whole call once the ride is
  // inside its edit window (see `editableUntil` on the ride).
  update: (id, patch) => api.patch(`/api/rides/${id}`, patch).then((r) => r.data),
  myDriving: () => api.get("/api/rides/mine/driving").then((r) => r.data),
  myRiding: () => api.get("/api/rides/mine/riding").then((r) => r.data),
  myHistory: (limit = 20) =>
    api.get("/api/rides/mine/history", { params: { limit } }).then((r) => r.data),
  // Its own call rather than `update(id, { returnAt: null })`: removal isn't a value a field can
  // hold, and a null in a partial patch is indistinguishable from a field the caller simply didn't
  // mention — which used to make any sparse patch cancel the trip home by accident.
  removeReturn: (id) => api.delete(`/api/rides/${id}/return`).then((r) => r.data),
  // `alsoCancelReturn` is asked, never assumed: the trip home is a separate ride with its own
  // riders, and "my morning changed" isn't "I'm not driving today".
  cancel: (id, alsoCancelReturn = false) =>
    api.post(`/api/rides/${id}/cancel`, null, { params: { alsoCancelReturn } }).then((r) => r.data),
  complete: (id) => api.post(`/api/rides/${id}/complete`).then((r) => r.data),
  bookings: (id) => api.get(`/api/rides/${id}/bookings`).then((r) => r.data),
  join: (id, pickupPoint) =>
    api.post(`/api/rides/${id}/join`, { pickupPoint }).then((r) => r.data),
};

export const bookings = {
  mine: () => api.get("/api/bookings/mine").then((r) => r.data),
  incoming: () => api.get("/api/bookings/incoming").then((r) => r.data),
  confirm: (id) => api.post(`/api/bookings/${id}/confirm`).then((r) => r.data),
  reject: (id) => api.post(`/api/bookings/${id}/reject`).then((r) => r.data),
  cancel: (id) => api.post(`/api/bookings/${id}/cancel`).then((r) => r.data),
};

export const points = {
  mine: (limit = 50) =>
    api.get("/api/points/mine", { params: { limit } }).then((r) => r.data),
  // `timeframe` is week | month | all. The server sums the ledger over the window rather than
  // reading the lifetime total on the user, which is what makes the pills re-rank anything.
  // What earns points and what points buy, for any signed-in user. A narrower projection of the
  // admin-only policy: rates and enabled tier labels, never payloads or provider ids.
  rules: () => api.get("/api/points/rules").then((r) => r.data),
  // The caller's true position, which the board cannot give once it is trimmed to boardSize.
  // 204 (no content) means genuinely unranked, so axios hands back "" — normalised to null here.
  myStanding: (timeframe = "all") =>
    api.get("/api/points/leaderboard/me", { params: { timeframe } })
      .then((r) => r.data || null),
  // `limit` is optional and usually omitted: the server falls back to the configured board size,
  // which is what makes that admin setting mean something. Passing one only ever asks for fewer.
  individuals: (limit, timeframe = "all") =>
    api.get("/api/points/leaderboard/individuals", {
      params: limit == null ? { timeframe } : { limit, timeframe },
    }).then((r) => r.data),
};

export const metrics = {
  snapshot: () => api.get("/api/metrics/snapshot").then((r) => r.data),
};

export const notifications = {
  list: (limit = 50) =>
    api.get("/api/notifications", { params: { limit } }).then((r) => r.data),
  count: () => api.get("/api/notifications/count").then((r) => r.data),
  // Clears the unread badge; returns the refreshed counts.
  markRead: () => api.post("/api/notifications/read").then((r) => r.data),
};

export const match = {
  recommended: (limit = 3) =>
    api.get("/api/match/recommended", { params: { limit } }).then((r) => r.data),
  rank: (rideIds) =>
    api.post("/api/match/rank", { rideIds }).then((r) => r.data),
};

export const locations = {
  post: (payload) => api.post("/api/locations", payload).then((r) => r.data),
  latest: (rideId) => api.get(`/api/locations/${rideId}/latest`).then((r) => r.data),
  stop: (rideId) => api.delete(`/api/locations/${rideId}`).then((r) => r.data),
};

/**
 * The points scheme's rules. Named for the resource rather than the audience — a URL says what a
 * thing is, not who may touch it — and guarded server-side by a class-level @PreAuthorize on
 * PointsPolicyController, so a non-admin reaching these gets a 403 rather than a partial answer.
 * The hidden sidebar tab is a courtesy, not the control.
 */
export const pointsPolicy = {
  get: () => api.get("/api/points/policy").then((r) => r.data),
  save: (policy) => api.put("/api/points/policy", policy).then((r) => r.data),
  rewardProviders: () => api.get("/api/points/policy/reward-providers").then((r) => r.data),
};

/**
 * The one global interface version, "v1" or "v2" (plan §0, decision 1).
 *
 * The read is public — permitted without a session in SecurityConfig — because the login page has
 * to follow the switch too: someone signed out must land on the same interface everyone else is
 * using, and there is no account to ask yet. It returns only the version, nothing about who set it.
 * The write is ADMIN only, enforced server-side; the Profile section that calls it is hidden from
 * everyone else as a courtesy, not as the control.
 *
 * The wire format is the enum ("V1"/"V2"); the frontend works in lowercase because that is what
 * `<html data-ui>` and the stored copy hold, so the conversion lives here and nowhere else.
 */
export const interfaceVersion = {
  get: () =>
    api.get("/api/interface-version").then((r) => String(r.data?.version || "").toLowerCase()),
  set: (version) =>
    api.put("/api/interface-version", { version: String(version).toUpperCase() }).then((r) => r.data),
};
